import { Star, ArrowUpRight, Quote } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { GOOGLE_LISTINGS, PRIMARY_LISTING, REVIEWS } from "@/data/reviews";

const Stars = ({ size = 16, testid }) => (
  <span className="inline-flex items-center gap-1 text-copper" aria-label="5 out of 5 stars" data-testid={testid}>
    {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={size} fill="currentColor" strokeWidth={0} />)}
  </span>
);

const ListingCard = ({ listing, i }) => (
  <a
    href={listing.url}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex items-center justify-between gap-6 border border-bone/15 px-6 py-5 transition-colors duration-300 hover:border-copper"
    data-testid={`review-listing-${listing.id}`}
  >
    <div>
      <p className="text-xs tracking-[0.25em] uppercase font-heading text-bone/50">{listing.place}</p>
      <p className="mt-1 font-heading font-bold text-base">{listing.name}</p>
      <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-bone/70">
        <span className="inline-flex items-center gap-2 whitespace-nowrap"><Stars size={12} /> {listing.score}</span>
        {listing.count && <span className="text-bone/45 whitespace-nowrap" data-testid={`review-listing-count-${listing.id}`}>· {listing.count} Google reviews</span>}
      </p>
    </div>
    <ArrowUpRight size={18} className="shrink-0 text-bone/40 transition-[color,transform] duration-300 group-hover:text-copper group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const ReviewCard = ({ review, i }) => (
  <Reveal delay={i * 0.08}>
    <blockquote className="h-full border border-bone/15 p-7 flex flex-col gap-5" data-testid={`review-card-${i}`}>
      <Quote size={20} className="text-copper" />
      <p className="text-sm md:text-base font-light leading-relaxed text-bone/80 flex-1">{review.text}</p>
      <footer className="flex items-center justify-between gap-4 text-xs">
        <span className="font-heading font-bold uppercase tracking-wide" data-testid={`review-author-${i}`}>{review.author}</span>
        <span className="flex items-center gap-3 text-bone/45"><Stars size={11} />{review.when}</span>
      </footer>
    </blockquote>
  </Reveal>
);

const Reviews = () => (
  <section id="reviews" className="relative bg-ink border-t border-bone/10 px-6 md:px-12 lg:px-20 py-28 md:py-36" data-testid="reviews-section">
    <Reveal><Chapter index="06" title="Word of Mouth" testid="reviews-chapter" /></Reveal>

    <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
      <Reveal delay={0.1} className="lg:col-span-7">
        <div className="flex items-end gap-5">
          <span className="font-heading font-black leading-none text-[6rem] sm:text-[8rem] lg:text-[10rem] tracking-tighter" data-testid="reviews-score">{PRIMARY_LISTING.score}</span>
          <div className="pb-3 sm:pb-5">
            <Stars size={22} testid="reviews-stars" />
            <p className="mt-3 text-xs tracking-[0.25em] uppercase font-heading text-bone/55" data-testid="reviews-count">
              {PRIMARY_LISTING.count} Google reviews
            </p>
          </div>
        </div>
        <h2 className="mt-8 text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-[1.05] max-w-xl" data-testid="reviews-heading">
          Rated five stars by the people who{" "}
          <span className="font-editorial italic font-medium text-copper">walk on it.</span>
        </h2>
        <p className="mt-6 max-w-md text-sm md:text-base font-light text-bone/60 leading-relaxed">
          Architects, contractors and homeowners rate our Kota yard 5.0 on Google.
          Every review is public — read them straight from the source.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href={PRIMARY_LISTING.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-read-button"
            className="group relative overflow-hidden inline-flex items-center gap-3 bg-copper px-8 py-4 text-xs tracking-[0.25em] uppercase font-heading font-bold text-ink"
          >
            <span className="relative z-10 flex items-center gap-2 transition-colors duration-300 group-hover:text-bone">Read reviews on Google <ArrowUpRight size={14} /></span>
            <span className="absolute inset-0 bg-ink translate-y-full transition-transform duration-300 ease-out group-hover:translate-y-0" />
          </a>
          <a
            href={PRIMARY_LISTING.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-write-button"
            className="inline-flex items-center gap-2 border border-bone/30 px-8 py-4 text-xs tracking-[0.25em] uppercase font-heading font-bold transition-colors duration-300 hover:border-copper hover:text-copper"
          >
            Write a review
          </a>
        </div>
      </Reveal>

      <Reveal delay={0.2} className="lg:col-span-5 lg:pt-6 flex flex-col gap-4" data-testid="reviews-listings">
        <p className="text-xs tracking-[0.25em] uppercase font-heading text-bone/40 mb-2">Our Google listings</p>
        {GOOGLE_LISTINGS.map((l, i) => <ListingCard key={l.id} listing={l} i={i} />)}
      </Reveal>
    </div>

    {REVIEWS.length > 0 && (
      <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
        {REVIEWS.map((r, i) => <ReviewCard key={r.author + i} review={r} i={i} />)}
      </div>
    )}
  </section>
);

export default Reviews;
